import { clientLogger } from '@/lib/clientLogger';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { CreateEstimateDto, InvoiceItemDto, JobContactInputDTO, ContactDTO, ContactCreateDTO, CompanyInvoiceDTO, JobItemOperationDTO } from '@/types';
import { toDateInputValue } from '@/lib/dateUtils';
import dayjs from 'dayjs';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { X, Calendar, FileText, Plus, Trash2, User } from 'lucide-react';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { ContactFormModal } from './contacts/ContactFormModal';
import { ContactSearchModal } from './contacts/ContactSearchModal';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useSplitScreenBounds } from '@/lib/splitScreen';
import { createPortal } from 'react-dom';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useVisualKeyboardOpen } from '@/hooks/useVisualKeyboardOpen';
import { useSectionOrder } from '@/hooks/useSectionOrder';
import { ReorderableSections, ReorderButton } from './ui/ReorderableSections';
import { useGoogleMapsAutocomplete, getAddressWithZipCode } from '@/hooks/useGoogleMapsAutocomplete';
import { MapPin } from 'lucide-react';
import { JobItemsSection } from './ui/JobItemsSection';

interface NewEstimateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onEstimateCreated: () => void;
    /** Pre-selected contact (e.g. "New Estimate" from a contact's details). */
    initialContact?: ContactDTO;
}

interface PendingContact {
    id: string;
    contact: ContactDTO | { id: string; name: string; phone: string; email?: string; notes?: string };
    createPayload?: ContactCreateDTO;
    notes?: string;
}

const DEFAULT_NEW_ESTIMATE_SECTION_ORDER = ['dates', 'location', 'contacts', 'jobType', 'notes', 'items'] as const;
type NewEstimateSectionId = typeof DEFAULT_NEW_ESTIMATE_SECTION_ORDER[number];
const NEW_ESTIMATE_SECTION_LABELS: Record<NewEstimateSectionId, string> = {
    dates: 'Schedule',
    location: 'Location',
    contacts: 'Contacts',
    jobType: 'Job Type',
    notes: 'Notes',
    items: 'Items',
};

export function NewEstimateModal({ isOpen, onClose, onEstimateCreated, initialContact }: NewEstimateModalProps) {
    const isMobile = useIsMobile();
    // Split screen open: the form covers only the side it was opened from.
    const splitBounds = useSplitScreenBounds();
    // Same as NewJob: buttons pinned on mobile, moved into the form while the keyboard is up.
    const isVirtualKeyboardOpen = useVisualKeyboardOpen(isOpen && isMobile);
    const pinnedFooter = isMobile && !isVirtualKeyboardOpen;
    // Each user can arrange the form's boxes ("Reorder" in the header). Saved on this device.
    const sectionOrder = useSectionOrder('newEstimate', DEFAULT_NEW_ESTIMATE_SECTION_ORDER, isOpen);
    const { selectedCompanyId } = useGlobalCompany();
    const companyTimezone = useCompanyTimezone()?.trim();
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    // Form state — default "today" refined when company invoice timezone loads (see effect below)
    const [date, setDate] = useState(() => toDateInputValue(new Date()));
    const [expirationDate, setExpirationDate] = useState('');
    const [notes, setNotes] = useState('');
    const [address, setAddress] = useState('');
    const [unitName, setUnitName] = useState('');
    const [jobTypeId, setJobTypeId] = useState('');
    const [items, setItems] = useState<(InvoiceItemDto & { id: string })[]>([]);
    const [pendingContacts, setPendingContacts] = useState<PendingContact[]>([]);

    // Modal states
    const [isContactFormOpen, setContactFormOpen] = useState(false);
    const [isContactSearchOpen, setContactSearchOpen] = useState(false);

    const layer = useModalLayer({
        enabled: isOpen && !isContactFormOpen && !isContactSearchOpen,
        onClose: () => { onClose(); },
    });

    const handleClose = () => {
        layer.onClose();
    };

    /** Avoid re-applying default estimate date on every render; reset when modal closes. */
    const estimateDateDefaultsAppliedKeyRef = useRef<string | null>(null);

    // Reset form when modal opens (estimate date is set in a separate effect using company timezone when available)
    useEffect(() => {
        if (!isOpen) {
            estimateDateDefaultsAppliedKeyRef.current = null;
            return;
        }
        setExpirationDate('');
        setNotes('');
        setAddress('');
        setUnitName('');
        setJobTypeId('');
        setItems([]);
        setPendingContacts(initialContact ? [{ id: initialContact.id, contact: initialContact }] : []);
        // Reset on open only — a refetched initialContact must not wipe a form in progress.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Fetch company invoice settings for tax rate (includes timezone for default estimate date)
    const { data: companyInvoiceSettings } = useQuery<CompanyInvoiceDTO | null>({
        queryKey: ['company-invoice-settings', selectedCompanyId],
        queryFn: () => apiService.getCompanyInvoiceSettings(),
        enabled: isOpen && !!selectedCompanyId,
    });
    const taxRate = companyInvoiceSettings?.tax_rate ?? 0;

    // Default estimate date to "today" in company timezone when company defines one; otherwise browser-local today.
    useEffect(() => {
        if (!isOpen) return;
        if (!selectedCompanyId) {
            setDate(toDateInputValue(new Date()));
            return;
        }
        const tz = companyTimezone;
        const keySuffix = tz ?? 'local';
        const applyKey = `${selectedCompanyId}-${keySuffix}`;
        if (estimateDateDefaultsAppliedKeyRef.current === applyKey) return;
        estimateDateDefaultsAppliedKeyRef.current = applyKey;

        if (tz) {
            setDate(dayjs().tz(tz).format('YYYY-MM-DD'));
        } else {
            setDate(toDateInputValue(new Date()));
        }
    }, [companyTimezone, isOpen, selectedCompanyId]);

    // Fetch job types
    const { data: jobTypes } = useQuery({
        queryKey: ['jobTypes', selectedCompanyId],
        queryFn: () => apiService.getJobTypesByCompany(),
        enabled: isOpen && !!selectedCompanyId,
    });

    // Create mutation
    const createMutation = useMutation({
        mutationFn: (data: CreateEstimateDto) => apiService.createEstimate(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['estimates'] });
            onEstimateCreated();
            onClose();
            addNotification(notificationService.success('Estimate created', 'The estimate was created successfully.'));
        },
        onError: (error: any) => {
            clientLogger.error('Failed to create estimate:', error);
            addNotification(notificationService.error('Failed to create estimate', error.message ?? 'Please try again.'));
        },
    });

    // Simple counter for temporary IDs (only used for React keys and state management)
    const nextItemIdRef = useRef(1);
    const nextContactIdRef = useRef(1);
    const addressInputRef = useRef<HTMLInputElement>(null);

    // Address Autocomplete
    const handlePlaceSelect = useCallback((place: google.maps.places.PlaceResult) => {
        const addressWithZip = getAddressWithZipCode(place);
        if (addressWithZip) {
            setAddress(addressWithZip);
        }
    }, []);

    useGoogleMapsAutocomplete({
        inputRef: addressInputRef,
        onPlaceSelect: handlePlaceSelect,
        isActive: isOpen,
    });

    // Sync address input
    useEffect(() => {
        const inputElement = addressInputRef.current;
        if (!inputElement) return;

        const handleInput = (e: Event) => {
            const target = e.target as HTMLInputElement;
            if (target.value !== address) {
                setAddress(target.value);
            }
        };

        inputElement.addEventListener('input', handleInput);
        return () => inputElement.removeEventListener('input', handleInput);
    }, [address]);

    // Item management
    const handleItemsChange = async (operations: JobItemOperationDTO[]) => {
        setItems(prev => {
            let newItems = [...prev];
            for (const op of operations) {
                if (op.operation === 'Add') {
                    newItems.push({
                        ...op.item,
                        id: `item-${nextItemIdRef.current++}`
                    } as any);
                } else if (op.operation === 'Edit') {
                    newItems = newItems.map(item => item.id === op.item.id ? { ...item, ...op.item } as any : item);
                } else if (op.operation === 'Remove') {
                    newItems = newItems.filter(item => item.id !== op.item.id);
                }
            }
            return newItems;
        });
        return Promise.resolve();
    };

    const handleContactResolution = (resolution: import('./contacts/ContactFormModal').ContactResolution) => {
        const tempId = `contact-${nextContactIdRef.current++}`;

        switch (resolution.action) {
            case 'create': {
                const entry: PendingContact = {
                    id: tempId,
                    contact: {
                        id: tempId,
                        name: resolution.contact.name,
                        phone: resolution.contact.phone,
                        additional_phone: resolution.contact.additional_phone,
                        email: resolution.contact.email,
                    },
                    createPayload: {
                        name: resolution.contact.name,
                        phone: resolution.contact.phone,
                        additional_phone: resolution.contact.additional_phone,
                        email: resolution.contact.email,
                        notes: resolution.contact.notes,
                    },
                    notes: resolution.assignmentNotes
                };
                setPendingContacts(prev => [...prev, entry]);
                break;
            }
            case 'useExisting': {
                if (pendingContacts.some(item => item.id === resolution.contactId)) return;

                const entry: PendingContact = {
                    id: resolution.contactId,
                    contact: resolution.contact ? {
                        id: resolution.contactId,
                        name: resolution.contact.name,
                        phone: resolution.contact.phone || '',
                        email: resolution.contact.email
                    } : {
                        id: resolution.contactId,
                        name: 'Loading...',
                        phone: ''
                    },
                    notes: resolution.assignmentNotes
                };
                setPendingContacts(prev => [...prev, entry]);
                break;
            }
        }
        setContactFormOpen(false);
    };

    const handleSelectExistingContact = (contact: ContactDTO) => {
        setContactSearchOpen(false);
        if (pendingContacts.some(item => item.id === contact.id)) return;
        const entry: PendingContact = {
            id: contact.id,
            contact,
            notes: undefined,
        };
        setPendingContacts(prev => [...prev, entry]);
    };

    const removeContact = (id: string) => {
        setPendingContacts(prev => prev.filter(c => c.id !== id));
    };

    // Submit handler
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedCompanyId) {
            addNotification(notificationService.error('No company selected', 'Please select a company first.'));
            return;
        }

        // Transform items
        const itemsDto: InvoiceItemDto[] = items
            .filter(item => item.name.trim() !== '')
            .map(item => ({
                name: item.name,
                description: item.description,
                price: item.price,
                quantity: item.quantity,
                discount: item.discount,
                discount_type: item.discount_type,
                taxable: item.taxable,
            }));

        // Transform contacts
        const contactsDto: JobContactInputDTO[] = pendingContacts.map(pending => {
            if (pending.createPayload) {
                return {
                    id: undefined,
                    contact: pending.createPayload,
                    notes: pending.notes,
                };
            } else {
                return {
                    id: pending.id,
                    notes: pending.notes,
                };
            }
        });

        const payload: CreateEstimateDto = {
            company_id: selectedCompanyId,
            estimate_number: '', // Auto-generated by backend
            date: date,
            expiration_date: expirationDate || undefined,
            notes: notes || undefined,
            address: address || undefined,
            unit_name: unitName || undefined,
            job_type_id: jobTypeId || undefined,
            items: itemsDto.length > 0 ? itemsDto : undefined,
            contacts: contactsDto.length > 0 ? contactsDto : undefined,
        };

        createMutation.mutate(payload);
    };

    if (!isOpen) return null;

    const paddingClass = isMobile ? 'p-3' : 'p-3';
    const gapClass = isMobile ? 'gap-3' : 'gap-3';
    const modalMaxWidth = isMobile ? 'max-w-full' : 'max-w-4xl';

    const footerButtons = (
        <>
            <button
                type="button"
                onClick={handleClose}
                className="btn-secondary flex-1 sm:flex-none sm:px-8"
                disabled={createMutation.isPending}
                style={{ minHeight: '44px' }}
            >
                Cancel
            </button>
            <button
                type="submit"
                form="estimate-form"
                disabled={createMutation.isPending || !selectedCompanyId}
                className="btn-primary flex-1 sm:flex-none sm:px-8"
                style={{ minHeight: '44px' }}
            >
                {createMutation.isPending ? (
                    <>
                        <LoadingSpinner />
                        Creating...
                    </>
                ) : (
                    'Create Estimate'
                )}
            </button>
        </>
    );

    return createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 z-50" style={{ ...layer.style, ...splitBounds }}>
                <div className={`bg-gray-50 sm:rounded-xl shadow-xl w-full ${modalMaxWidth} h-full ${splitBounds ? 'sm:max-h-full' : 'sm:max-h-[90vh] safe-area-top'} overflow-hidden flex flex-col`}>
                    {/* Header */}
                    <div className={`bg-white flex items-center justify-between ${paddingClass} border-b shrink-0`}>
                        <div className="flex-1 flex flex-col lg:flex-row lg:items-center lg:gap-6">
                            <div className="flex items-center gap-3">
                                <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-900 flex-shrink-0`}>New Estimate</h2>
                                {!isMobile && (
                                    <ReorderButton isReordering={sectionOrder.isReordering} onToggle={() => sectionOrder.setIsReordering((on) => !on)} />
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col items-end self-start">
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2"
                                style={{ minWidth: '44px', minHeight: '44px' }}
                            >
                                <X className="h-6 w-6" />
                            </button>
                            {isMobile && (
                                <ReorderButton isReordering={sectionOrder.isReordering} onToggle={() => sectionOrder.setIsReordering((on) => !on)} />
                            )}
                        </div>
                    </div>

                    {/* Form */}
                    <div className="flex-1 overflow-y-auto">
                        <form id="estimate-form" onSubmit={handleSubmit} className={paddingClass}>
                            <div className={`flex flex-col ${gapClass}`}>
                                {/* Boxes in the user's saved order ("Reorder" in the header) */}
                                <ReorderableSections
                                    sections={{
                                    dates: (
                                        <>
                                {/* DATES CARD */}
                                <div className="bg-white rounded-lg border border-gray-200 p-3">
                                    <div className="sm:space-y-2">
                                        <label className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                            <Calendar className="h-3.5 w-3.5 text-amber-500" />
                                            Schedule
                                        </label>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <label className="text-xs font-medium text-gray-600">Estimate Date</label>
                                                <input
                                                    type="date"
                                                    value={date}
                                                    onChange={(e) => setDate(e.target.value)}
                                                    className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-xs font-medium text-gray-600">Valid Until (Optional)</label>
                                                <input
                                                    type="date"
                                                    value={expirationDate}
                                                    onChange={(e) => setExpirationDate(e.target.value)}
                                                    className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                        </>
                                    ),
                                    location: (
                                        <>
                                {/* LOCATION CARD */}
                                <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
                                    <div className="space-y-1">
                                        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                            <MapPin className="h-3.5 w-3.5 text-rose-500" />
                                            Location
                                        </label>
                                        <div className="relative">
                                            <input
                                                ref={addressInputRef}
                                                type="text"
                                                value={address}
                                                onChange={(e) => setAddress(e.target.value)}
                                                className="w-full text-sm pl-8 pr-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none"
                                                placeholder="Enter address"
                                                autoComplete="off"
                                            />
                                            <MapPin className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                        </div>
                                        <input
                                            type="text"
                                            value={unitName}
                                            onChange={(e) => setUnitName(e.target.value)}
                                            className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none mt-1.5"
                                            placeholder="Unit/Business Name (optional)"
                                        />
                                    </div>
                                </div>
                                        </>
                                    ),
                                    contacts: (
                                        <>
                                {/* CONTACTS CARD */}
                                <div className="bg-white rounded-lg border border-gray-200 p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                            <User className="h-3.5 w-3.5 text-indigo-500" />
                                            Contacts
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setContactFormOpen(true)}
                                                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-0.5"
                                            >
                                                <Plus className="h-3 w-3" />
                                                New
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setContactSearchOpen(true)}
                                                className="text-xs text-gray-500 hover:text-gray-700 font-medium flex items-center gap-0.5"
                                            >
                                                <User className="h-3 w-3" />
                                                Existing
                                            </button>
                                        </div>
                                    </div>
                                    {pendingContacts.length === 0 ? (
                                        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-2 text-center">
                                            <p className="text-xs text-gray-500">No contacts added</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-1.5">
                                            {pendingContacts.map((c, index) => (
                                                <div key={c.id} className="flex items-center justify-between px-2 py-1.5 rounded-lg text-sm bg-gray-50 border border-gray-200">
                                                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                                        <User className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                                        <span className="font-medium truncate text-gray-900 text-sm">{c.contact.name}</span>
                                                        {index === 0 && <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Primary</span>}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeContact(c.id)}
                                                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                        </>
                                    ),
                                    jobType: (
                                        <>
                                {/* JOB TYPE CARD */}
                                <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-1">
                                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                        <FileText className="h-3.5 w-3.5 text-cyan-500" />
                                        Job Type (Optional)
                                    </label>
                                    <select
                                        value={jobTypeId}
                                        onChange={(e) => setJobTypeId(e.target.value)}
                                        className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none"
                                    >
                                        <option value="">Select Job Type</option>
                                        {jobTypes?.map((jt: any) => (
                                            <option key={jt.id} value={jt.id}>{jt.name}</option>
                                        ))}
                                    </select>
                                </div>
                                        </>
                                    ),
                                    notes: (
                                        <>
                                {/* NOTES CARD */}
                                <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-1">
                                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                        <FileText className="h-3.5 w-3.5 text-orange-500" />
                                        Notes
                                    </label>
                                    <textarea
                                        value={notes}
                                        onChange={(e) => {
                                            setNotes(e.target.value);
                                            e.target.style.height = 'auto';
                                            e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                                        }}
                                        className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none resize-none"
                                        rows={2}
                                        placeholder="Add any notes about this estimate..."
                                    />
                                </div>
                                        </>
                                    ),
                                    items: (
                                        <>
                                {/* ITEMS SECTION */}
                                <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-3">
                                    <JobItemsSection
                                        items={items}
                                        companyId={selectedCompanyId || undefined}
                                        taxRate={taxRate}
                                        defaultItemTaxable={companyInvoiceSettings?.default_item_taxable ?? true}
                                        onItemsChange={handleItemsChange}
                                        isEditable={true}
                                    />
                                </div>
                                        </>
                                    ),
                                    }}
                                    order={sectionOrder.order}
                                    labels={NEW_ESTIMATE_SECTION_LABELS}
                                    isReordering={sectionOrder.isReordering}
                                    isCustom={sectionOrder.isCustom}
                                    isMobile={isMobile}
                                    onReorder={sectionOrder.reorder}
                                    onReset={sectionOrder.reset}
                                    onDone={() => sectionOrder.setIsReordering(false)}
                                    listClassName={`flex flex-col ${gapClass}`}
                                />
                            </div>

                            {!pinnedFooter && (
                                <div className="mt-4 flex items-center justify-between sm:justify-end gap-3 pt-4 pb-4 sm:pb-0 border-t border-gray-200">
                                    {footerButtons}
                                </div>
                            )}
                        </form>
                    </div>

                    {pinnedFooter && (
                        <div className="shrink-0 flex items-center gap-3 bg-white border-t border-gray-200 px-3 pt-3 pb-[max(0.75rem,var(--safe-area-inset-bottom),var(--android-safe-area-bottom))]">
                            {footerButtons}
                        </div>
                    )}
                </div>
            </div>

            {/* Contact Form Modal */}
            <ContactFormModal
                isOpen={isContactFormOpen}
                onClose={() => setContactFormOpen(false)}
                onSubmit={() => { }}
                onResolution={handleContactResolution}
                isSubmitting={false}
            />

            {/* Contact Search Modal */}
            <ContactSearchModal
                isOpen={isContactSearchOpen}
                onClose={() => setContactSearchOpen(false)}
                onSelect={handleSelectExistingContact}
            />
        </LayerDepthContext.Provider>,
        document.body
    );
}
